import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { toast } from '@buffer-mono/popcorn'
import { BufferTrackerReact, Client, Product } from '@buffer-mono/tracking-plan'

import {
  useAccount,
  useHasPaymentDetails,
  useIsOneBufferOrg,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { isOfType } from '~publish/helpers/typeGuards'
import { isFreeUser } from '~publish/helpers/user'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { navigateToBilling } from '~publish/helpers/navigateToBilling'
import { Limits } from '~publish/pages/Create/Feed/constants/constants'
import type { FeedCTA } from '~publish/pages/Create/Feed/constants/constants'
import { useCreateNewFeedCollection } from '~publish/pages/Create/Feed/hooks'
import {
  parseErrorMessage,
  useCreateFeed,
} from '~publish/pages/Create/Feed/hooks/useCreateFeed'
import type { WrappedCreateFeedResponse } from '~publish/pages/Create/Feed/hooks/useCreateFeed'
import { useDeleteFeed } from '~publish/pages/Create/Feed/hooks/useDeleteFeed'

interface UseFeedsNavigationProps {
  collectionsCount: number
  totalFeeds: number
}

interface UseFeedsNavigationResult {
  tier: 'free' | 'paid'
  limitReached: boolean
  collectionsLimitReached: boolean
  totalRemainingFeeds: number
  handleCreateFeed: (args: {
    feedUrl: string
    cta: FeedCTA
  }) => Promise<WrappedCreateFeedResponse>
  handleDeleteFeed: (feedId: string) => Promise<void>
  handleCreateCollection: (name: string) => Promise<void>
  handleUpgradeClick: (args: { cta: CTA; upgradePathName: string }) => void
}

export function useFeedsNavigation({
  collectionsCount,
  totalFeeds,
}: UseFeedsNavigationProps): UseFeedsNavigationResult {
  const organizationId = useOrganizationId()
  const navigate = useNavigate()
  const openGlobalModal = useOpenGlobalModal()

  const { account } = useAccount()
  const tier = isFreeUser(account) ? 'free' : 'paid'
  const collectionsLimitReached = collectionsCount >= Limits.collections[tier]
  const totalRemainingFeeds = Limits.feeds[tier].total - totalFeeds
  const limitReached = totalFeeds >= Limits.feeds[tier].total

  const isOneBufferOrg = useIsOneBufferOrg()
  const hasPaymentDetails = useHasPaymentDetails()
  const commonTrackingProperties = useCommonTrackingProperties()

  const baseTrackingProperties = useMemo(
    () => ({
      clientName: Client.PublishWeb,
      product: Product.Publish,
      currentCollectionsCount: collectionsCount,
      hasPaymentDetails,
      isFreeUser: tier === 'free',
      ...commonTrackingProperties,
    }),
    [commonTrackingProperties, collectionsCount, hasPaymentDetails, tier],
  )

  const handleUpgradeClick = useCallback(
    ({ cta, upgradePathName }: { cta: CTA; upgradePathName: string }): void => {
      const trackingProperties = {
        ...baseTrackingProperties,
        cta,
        upgradePathName,
      }
      if (isOneBufferOrg) {
        BufferTrackerReact.cTAClicked(trackingProperties)
        openGlobalModal(MODALS.planSelector, trackingProperties)
      } else {
        navigateToBilling(cta)
      }
    },
    [baseTrackingProperties, isOneBufferOrg, openGlobalModal],
  )

  const [createFeed] = useCreateFeed()
  const handleCreateFeed = useCallback(
    async ({
      feedUrl,
      cta,
    }: {
      feedUrl: string
      cta: FeedCTA
    }): Promise<WrappedCreateFeedResponse> => {
      if (!organizationId) {
        throw new Error('Organization ID is required')
      }

      if (limitReached) {
        throw new Error('Failed to add feed: Feed limit reached.')
      }

      const result = await createFeed({
        variables: {
          input: {
            organizationId,
            rssAtom: { url: feedUrl },
          },
        },
      })

      if (result.success) {
        toast.success('Successfully added feed')
        BufferTrackerReact.feedSubscribed({
          product: 'publish',
          organizationId,
          feedId: result.data.feed.id,
          feedCollectionId: '',
          feedUrl,
          feedQuery: undefined,
          feedType: 'rssAtom',
          clientName: 'publishWeb',
          cta,
        })
        navigate('/create/feeds/' + result.data.feed.id)
        return { feedId: result.data.feed.id, error: null }
      }
      return { feedId: null, error: parseErrorMessage(result.error) }
    },
    [createFeed, organizationId, limitReached, navigate],
  )

  const [deleteFeed] = useDeleteFeed()
  const handleDeleteFeed = useCallback(
    async (deletingFeedId: string): Promise<void> => {
      if (!organizationId) return

      const { error } = await deleteFeed({
        variables: { input: { organizationId, id: deletingFeedId } },
      })
      if (error) {
        toast.error('Failed to delete feed')
      }
    },
    [deleteFeed, organizationId],
  )

  const [createNewFeedCollection] = useCreateNewFeedCollection()
  const handleCreateCollection = useCallback(
    async (name: string): Promise<void> => {
      if (!organizationId) return

      const { data, error } = await createNewFeedCollection({
        variables: {
          input: {
            name,
            organizationId,
            feedIds: [],
          },
        },
      })

      if (error) {
        toast.error('Failed to create feed collection')
        return
      }

      if (isOfType(data, 'FeedCollectionActionSuccess')) {
        navigate('/create/feed/collections/' + data.feedCollection.id)
        BufferTrackerReact.feedCollectionCreated({
          product: 'publish',
          organizationId,
          feedCollectionId: data.feedCollection.id,
          feedCollectionName: data.feedCollection.name,
          clientName: 'publishWeb',
        })
      }
    },
    [createNewFeedCollection, organizationId, navigate],
  )

  return {
    tier,
    limitReached,
    collectionsLimitReached,
    totalRemainingFeeds,
    handleCreateFeed,
    handleDeleteFeed,
    handleCreateCollection,
    handleUpgradeClick,
  }
}
